import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
} from "../../lib/consent";

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c.decided) {
      // Delay so the page can paint first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setAnalytics(c.analytics);
    setMarketing(c.marketing);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setShowPrefs(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    setShowPrefs(false);
  };
  const handleSavePrefs = () => {
    setConsent({ analytics, marketing });
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Compact banner */}
      {!showPrefs && (
        <div
          data-testid="cookie-banner"
          role="dialog"
          aria-labelledby="cookie-title"
          className="fixed bottom-4 left-4 right-4 z-[70] sm:bottom-6 sm:left-auto sm:right-6 sm:w-[min(420px,calc(100vw-3rem))]"
        >
          <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 shadow-[0_30px_60px_-25px_rgba(26,37,30,0.35)]">
            <div className="flex items-start gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--surface-secondary))] text-[hsl(var(--sand-dark))]">
                <Cookie className="h-5 w-5" strokeWidth={1.6} />
              </span>
              <div className="flex-1">
                <p
                  id="cookie-title"
                  className="font-display text-xl text-[hsl(var(--ink))]"
                >
                  {t("cookie.title")}
                </p>
                <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">
                  {t("cookie.body")}{" "}
                  <Link
                    to="/cookies"
                    className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
                  >
                    {t("cookie.policy")}
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))]"
              >
                {t("cookie.acceptAll")}
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-reject-all"
                className="flex-1 rounded-full border border-[hsl(var(--ink))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
              >
                {t("cookie.essentialOnly")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowPrefs(true)}
              data-testid="cookie-preferences"
              className="mt-3 w-full text-center text-xs text-[hsl(var(--ink-soft))] underline-offset-4 hover:text-[hsl(var(--ink))] hover:underline"
            >
              {t("cookie.customize")}
            </button>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {showPrefs && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-[hsl(var(--ink))]/40 p-4 backdrop-blur-sm sm:items-center"
          data-testid="cookie-prefs-modal"
        >
          <div className="w-full max-w-md rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                  Cookie voorkeuren
                </p>
                <h3 className="mt-2 font-display text-2xl text-[hsl(var(--ink))]">
                  Kies wat je toestaat
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                aria-label="Sluit"
                className="rounded-full p-1.5 text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-secondary))]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-3">
              <Row
                title="Noodzakelijk"
                description="Altijd actief. Nodig voor basisfunctionaliteit zoals navigatie en formulieren."
                checked
                disabled
              />
              <Row
                title="Analytics"
                description="Helpt ons begrijpen welke pagina's worden bezocht en welke campagnes werken."
                checked={analytics}
                onChange={() => setAnalytics((v) => !v)}
                testId="cookie-toggle-analytics"
              />
              <Row
                title="Marketing"
                description="Onthoudt jouw rol en herkomst tussen sessies om betere aanbiedingen te doen."
                checked={marketing}
                onChange={() => setMarketing((v) => !v)}
                testId="cookie-toggle-marketing"
              />
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSavePrefs}
                data-testid="cookie-save"
                className="flex-1 rounded-full bg-[hsl(var(--forest))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--background))] transition-all hover:bg-[hsl(var(--forest-dark))]"
              >
                Voorkeuren opslaan
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="flex-1 rounded-full border border-[hsl(var(--ink))] px-5 py-2.5 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
              >
                Accepteer alle
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Row = ({ title, description, checked, onChange, disabled, testId }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-4">
    <span className="flex-1">
      <span className="font-medium text-[hsl(var(--ink))]">{title}</span>
      <span className="mt-1 block text-xs text-[hsl(var(--ink-soft))]">
        {description}
      </span>
    </span>
    <span className="mt-1 inline-flex">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
        className="peer sr-only"
      />
      <span
        className={`relative inline-block h-5 w-9 rounded-full transition-all ${
          checked ? "bg-[hsl(var(--forest))]" : "bg-[hsl(var(--ink-soft))]/30"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
            checked ? "left-4" : "left-0.5"
          }`}
        />
      </span>
    </span>
  </label>
);

export default CookieConsent;
